import React from "react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {
  Badge, Button, Flex, Icon, IconButton, Spinner, Table, TableContainer, Tbody, Td, Text,
  Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import { MdAccountBalanceWallet, MdCheckCircleOutline, MdContentCopy } from "react-icons/md";
import Card from "horizon/components/card/Card";
import { formatAmount, formatSubjectLabel, getPayoutPhone, isMobileMoneySubject } from "./transactionsManageConstants";
import { BODY_CELL, HEAD_CELL } from "./transactionsManageStyles";

const COLUMNS = ["ID", "Wallet", "Sujet", "Téléphone", "Direction", "Montant", "Créée le", "Action"];

/**
 * Formats an ISO date as DD/MM/YYYY HH:mm or "—" when missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDate(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Renders the wallet cell with an icon and the wallet ID in monospace.
 * @param {Object} props - walletId (number)
 * @returns {React.ReactElement}
 */
function WalletCell({ walletId }) {
  const color = useColorModeValue("secondaryGray.700", "secondaryGray.400");
  return (
    <Flex align="center" gap="6px">
      <Icon as={MdAccountBalanceWallet} color="brand.500" w="16px" h="16px" />
      <Text fontFamily="mono" fontSize="xs" color={color}>{walletId != null ? `#${walletId}` : "—"}</Text>
    </Flex>
  );
}

/**
 * Copies a phone number to the clipboard and confirms with a toast.
 * @param {string} phone - Phone number to copy
 * @returns {void}
 */
function copyPhone(phone) {
  navigator.clipboard.writeText(phone);
  toast.success("Numéro copié");
}

/**
 * Renders the destination phone for mobile money rows with a copy button.
 * Legacy mobile money rows without a snapshot show a warning badge; other
 * subjects (Stripe payouts, bank transfers) show a dash.
 * @param {Object} props - transaction object
 * @returns {React.ReactElement}
 */
function PhoneCell({ transaction }) {
  const monoColor = useColorModeValue("secondaryGray.700", "secondaryGray.400");
  const phone = getPayoutPhone(transaction);
  if (phone) {
    return (
      <Flex align="center" gap="4px">
        <Text fontFamily="mono" fontSize="xs" color={monoColor}>{phone}</Text>
        <IconButton aria-label="Copier le numéro" icon={<Icon as={MdContentCopy} />} size="xs"
          variant="ghost" onClick={() => copyPhone(phone)} />
      </Flex>
    );
  }
  if (isMobileMoneySubject(transaction?.subject)) {
    return <Badge colorScheme="yellow" variant="subtle" borderRadius="6px">Voir profil artiste</Badge>;
  }
  return <Text fontSize="xs" color={monoColor}>—</Text>;
}

/**
 * Renders a single pending transaction row.
 * @param {Object} props - transaction, onApprove
 * @returns {React.ReactElement}
 */
function TransactionRow({ transaction, onApprove }) {
  const color = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const monoColor = useColorModeValue("secondaryGray.700", "secondaryGray.400");
  const credit = transaction?.increase;
  return (
    <Tr>
      <Td {...BODY_CELL}><Text fontFamily="mono" fontSize="xs" color={monoColor}>#{transaction?.id || "—"}</Text></Td>
      <Td {...BODY_CELL}><WalletCell walletId={transaction?.wallet_id} /></Td>
      <Td {...BODY_CELL}><Badge colorScheme="gray" variant="subtle" borderRadius="6px">{formatSubjectLabel(transaction?.subject)}</Badge></Td>
      <Td {...BODY_CELL}><PhoneCell transaction={transaction} /></Td>
      <Td {...BODY_CELL}>
        <Badge colorScheme={credit ? "green" : "orange"} variant="subtle" borderRadius="6px">
          {credit ? "+ Crédit" : "− Débit"}
        </Badge>
      </Td>
      <Td {...BODY_CELL} color={color}>{formatAmount(transaction)}</Td>
      <Td {...BODY_CELL} color={color}>{formatDate(transaction?.created_at)}</Td>
      <Td {...BODY_CELL}>
        <Button size="sm" colorScheme="green" leftIcon={<Icon as={MdCheckCircleOutline} />}
          onClick={() => onApprove?.(transaction)}>Valider</Button>
      </Td>
    </Tr>
  );
}

/**
 * Centered state message (loading spinner / error / empty).
 * @param {Object} props - loading (boolean), children (message node)
 * @returns {React.ReactElement}
 */
function StateBox({ loading, children }) {
  return <Flex align="center" justify="center" py="40px">{loading ? <Spinner color="brand.500" /> : children}</Flex>;
}

/**
 * Pending transactions table — handles loading/error/empty internally.
 * @param {Object} props - items, loading, error, onApprove
 * @returns {React.ReactElement}
 */
function TransactionsTable({ items, loading, error, onApprove }) {
  const empty = !items || items.length === 0;
  const headColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const errColor = useColorModeValue("red.500", "red.300");
  const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Card px="0" py="0" overflow="hidden">
      {loading && empty ? <StateBox loading><span /></StateBox> : null}
      {!loading && error && empty ? (
        <StateBox><Text color={errColor} fontSize="sm">{typeof error === "string" ? error : "Erreur lors du chargement"}</Text></StateBox>
      ) : null}
      {!loading && !error && empty ? (
        <StateBox><Text color={emptyColor} fontSize="sm">Aucun paiement en attente</Text></StateBox>
      ) : null}
      {!empty ? (
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>{COLUMNS.map((c) => <Th key={c} {...HEAD_CELL} color={headColor}>{c}</Th>)}</Tr>
            </Thead>
            <Tbody>
              {items.map((tx) => <TransactionRow key={tx.id} transaction={tx} onApprove={onApprove} />)}
            </Tbody>
          </Table>
        </TableContainer>
      ) : null}
    </Card>
  );
}

export default TransactionsTable;
